import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    try {
      const res = await client.get("/notifications");
      setItems(res.data.items);
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error("NotificationsContext.refresh failed:", e);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return;
    const interval = setInterval(refresh, 20000);
    return () => clearInterval(interval);
  }, [refresh, isAuthenticated]);

  const markRead = useCallback(async (id) => {
    await client.put(`/notifications/${id}/read`);
    refresh();
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    await client.put("/notifications/read-all");
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ items, unreadCount, refresh, markRead, markAllRead }),
    [items, unreadCount, refresh, markRead, markAllRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
